import React, { useEffect, useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { LibraryCard } from '@/lib/library/libraryCard';
import { sourceMeta } from '@/lib/library/sources';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { SourceGlyph } from './SourceGlyph';
import { ItemComments } from './ItemComments';
import { useLibrary } from './LibraryContext';

/**
 * Pick a space for an item. Shared by the detail view (Move to space) and the
 * long-press menu (Save to space). Root boards (Home) are not offered: an
 * item there is simply in your mind.
 */
export function LibrarySpacePicker({
  card,
  open,
  onOpenChange,
}: {
  card: LibraryCard | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { actions, boards } = useLibrary();
  const spaces = boards.filter((b) => b.parent_id !== null);
  // "New space": the row turns into a field; the space is made in Home and the item goes into it
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) return;
    setNaming(false);
    setName('');
    setBusy(false);
  }, [open]);

  const pick = async (boardId: string) => {
    onOpenChange(false);
    if (card) await actions.moveToBoard(card.item, boardId);
  };

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy || !actions.createSpace) return;
    setBusy(true);
    const board = await actions.createSpace(trimmed);
    if (!board) {
      setBusy(false);
      return;
    }
    onOpenChange(false);
    if (card) await actions.moveToBoard(card.item, board.id);
    toast.success(`Saved to ${board.name}`);
  };

  const row = 'flex h-12 w-full items-center rounded-[10px] px-3 text-left text-[15px] text-ink outline-none active:bg-wash focus-visible:ring-2 focus-visible:ring-signal';

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent className="bg-paper-raised">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-[17px] text-ink">Save to space</DrawerTitle>
          <DrawerDescription className="text-[13px] text-ink-3">Pick where this lives.</DrawerDescription>
        </DrawerHeader>
        <div className="max-h-[50vh] overflow-y-auto px-2 pb-[calc(16px+env(safe-area-inset-bottom))]">
          {actions.createSpace &&
            (naming ? (
              <form
                className="flex h-12 items-center gap-2 px-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  void create();
                }}
              >
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name the space"
                  aria-label="New space name"
                  maxLength={80}
                  autoFocus
                  enterKeyHint="done"
                  // 16px so Safari does not zoom in on focus
                  className="h-10 min-w-0 flex-1 rounded-[10px] border border-line bg-paper px-3 text-[16px] text-ink outline-none placeholder:text-ink-3 focus:border-signal"
                />
                <button
                  type="submit"
                  disabled={!name.trim() || busy}
                  className="h-10 shrink-0 rounded-full bg-ink px-4 text-[14px] font-medium text-paper outline-none transition-opacity disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-signal"
                >
                  {busy ? 'Saving' : 'Create'}
                </button>
              </form>
            ) : (
              <button type="button" onClick={() => setNaming(true)} className={`${row} gap-3 text-ink-2`}>
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-line">
                  <Plus className="h-4 w-4" strokeWidth={1.9} aria-hidden />
                </span>
                New space
              </button>
            ))}
          {spaces.length === 0 ? (
            <p className="px-2 py-6 text-center text-[14px] text-ink-3">You have no spaces yet.</p>
          ) : (
            spaces.map((b) => {
              const current = b.id === card?.boardId;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => pick(b.id)}
                  className={`${row} justify-between`}
                  aria-current={current ? 'true' : undefined}
                >
                  <span className="truncate">{b.name}</span>
                  {current && <Check className="h-4 w-4 text-ink-2" strokeWidth={2} aria-label="Current space" />}
                </button>
              );
            })
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

/** The comments on an item, in a sheet: what the long-press menu's Comment opens. */
export function LibraryCommentsSheet({
  card,
  open,
  onOpenChange,
}: {
  card: LibraryCard | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent className="bg-paper">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-[17px] text-ink">Comments</DrawerTitle>
          {card && (
            <DrawerDescription className="flex items-center gap-1.5 text-[13px] text-ink-3">
              <SourceGlyph source={card.source} className="h-3.5 w-3.5" />
              <span className="truncate">{card.title || sourceMeta(card.source).noun}</span>
            </DrawerDescription>
          )}
        </DrawerHeader>
        <div className="max-h-[60vh] overflow-y-auto px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
          {card && <ItemComments itemId={card.id} autoFocus />}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
